import React from 'react';
import {Box, Button, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {Organization} from "crqlar-react-common/models/organization/organization";

type BannerState = 'normal' | 'urgent' | 'expired';

export const SELF_SERVICE_TRIAL_DAYS = 30;
export const SELF_SERVICE_TRIAL_URGENT_DAYS = 7;
export const TRIAL_BANNER_HEIGHT = 48;

const MS_PER_DAY = 1000 * 60 * 60 * 24;

interface Props {
    organization?: Organization;
}

export interface SelfServiceTrialStatus {
    createdAt: Date;
    daysPassed: number;
    daysLeft: number;
    isExpired: boolean;
}

const toValidDate = (value?: Date | string): Date | undefined => {
    if (!value) {
        return undefined;
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
        return undefined;
    }

    return date;
};

const getUtcDayNumber = (date: Date) => {
    return Math.floor(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) / MS_PER_DAY);
};

export const getSelfServiceTrialStatus = (organization?: Organization): SelfServiceTrialStatus | undefined => {
    if (!organization?.selfService) {
        return undefined;
    }

    const createdAt = toValidDate(organization.insertDate);

    if (!createdAt) {
        return undefined;
    }

    // Compare calendar days in UTC so trial expiry is stable across DST boundaries.
    const daysPassed = Math.max(0, getUtcDayNumber(new Date()) - getUtcDayNumber(createdAt));
    const daysLeft = SELF_SERVICE_TRIAL_DAYS - daysPassed;

    return {
        createdAt,
        daysPassed,
        daysLeft,
        isExpired: daysLeft <= 0,
    };
};

const styles = {
    normal: {
        bgcolor: '#21C9E3',
        label: 'rgba(0,0,0,0.4)',
        sep: 'rgba(0,0,0,0.15)',
        text: 'rgba(0,0,0,0.6)',
        days: 'rgba(0,0,0,0.85)',
        btnBg: 'rgba(0,0,0,0.8)',
        btnBgHover: 'rgba(0,0,0,0.9)',
        btnColor: '#fff',
    },
    urgent: {
        bgcolor: '#FF9800',
        label: 'rgba(0,0,0,0.5)',
        sep: 'rgba(0,0,0,0.15)',
        text: 'rgba(0,0,0,0.7)',
        days: 'rgba(0,0,0,0.9)',
        btnBg: 'rgba(0,0,0,0.8)',
        btnBgHover: 'rgba(0,0,0,0.9)',
        btnColor: '#fff',
    },
    expired: {
        bgcolor: '#D32F2F',
        label: 'rgba(255,255,255,0.5)',
        sep: 'rgba(255,255,255,0.2)',
        text: 'rgba(255,255,255,0.8)',
        days: '#fff',
        btnBg: '#fff',
        btnBgHover: 'rgba(255,255,255,0.9)',
        btnColor: '#D32F2F',
    },
};

const TrialBanner: React.FC<Props> = ({organization}) => {

    const {t} = useLocalize();
    const navigate = useNavigate();
    const trialStatus = getSelfServiceTrialStatus(organization);

    if (!trialStatus) {
        return null;
    }

    const state: BannerState = trialStatus.isExpired
        ? 'expired'
        : trialStatus.daysLeft <= SELF_SERVICE_TRIAL_URGENT_DAYS
            ? 'urgent'
            : 'normal';


    const s = styles[state];

    return (
        <Box
            sx={{
                position: 'relative',
                zIndex: 500,
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: 48,
                px: '20px',
                bgcolor: s.bgcolor,
                borderBottom: '1px solid #fff',
                userSelect: 'none',
            }}
            className="hide-on-print"
        >
            <Typography
                sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.8px',
                    textTransform: 'uppercase',
                    color: s.label,
                    flexShrink: 0,
                }}
            >
                {state === 'expired' ? t('Trial expired') : t('Free trial')}
            </Typography>

            <Box
                sx={{
                    width: '1px',
                    height: 16,
                    mx: '12px',
                    bgcolor: s.sep,
                    flexShrink: 0,
                }}
            />

            <Typography
                sx={{
                    fontSize: '13px',
                    color: s.text,
                    whiteSpace: 'nowrap',
                }}
            >
                {state === 'expired' ? (
                    t('Your free trial has ended')
                ) : (
                    <>
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 700,
                                fontVariantNumeric: 'tabular-nums',
                                color: s.days,
                            }}
                        >
                            {trialStatus.daysLeft}
                        </Box>
                        {' '}{t('days left')}
                    </>
                )}
            </Typography>

            <Button
                variant="contained"
                size="small"
                onClick={() => navigate('/system/billing')}
                sx={{
                    ml: 'auto',
                    flexShrink: 0,
                    textTransform: 'none',
                    borderRadius: '100px',
                    bgcolor: s.btnBg,
                    color: s.btnColor,
                    boxShadow: 'none',
                    '&:hover': {
                        bgcolor: s.btnBgHover,
                        boxShadow: 'none',
                    },
                }}
            >
                {state === 'expired' ? t('Subscribe now') : t('Upgrade now')}
            </Button>
        </Box>
    );
};

export default TrialBanner;
